import { i18n } from 'boot/i18n';
import { chain, cloneDeep, isEmpty, isEqual } from 'lodash';
import { CLIENTS_NOTIFICATIONS_TYPES } from 'src/constants/notifications';
import RealTimeNotificationsDB from 'src/fb/realTimeNotifications-db';
import { auth as useAuthStore } from 'src/store/auth';
import { clients as useClientsStore } from 'src/store/clients';
import { network as useNetworkStore } from 'src/store/network';
import AppUtils from 'src/utils/AppUtils';
import { logEvent } from 'src/utils/LogEvent';
import { computed, shallowRef, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useCollection } from 'vuefire';

import { notifyWarning } from './interactions';

/**
 * General composable that stores the commonly used methods for triggering notifications.
 */
export function useTriggerNotifications() {
  const authStore = useAuthStore();
  const clientsStore = useClientsStore();

  const userDetails = computed(() => authStore.userDetails);

  /**
   * @param {string} client - client id
   * @param {'clientAdded' | 'clientUpdated' | 'clientRemoved'} notificationType
   * @returns {Promise<void>}
   */
  async function createClientsTriggerNotifications(client, notificationType) {
    const realTimeNotificationsDb = new RealTimeNotificationsDB();
    const newNotification = await realTimeNotificationsDb.create({
      clinic: userDetails.value.clinic,
      client,
      context: RealTimeNotificationsDB.context.CLIENTS,
      type: notificationType,
      isViewed: false,
    });

    await clientsStore.addClientsNotificationToIgnore(newNotification.id);

    return newNotification;
  }

  return {
    createClientsTriggerNotifications,
  };
}

/**
 * Initialize clients trigger notifications bindings and its processing.
 */
export function useClientsTriggerNotifications() {
  const router = useRouter();

  const authStore = useAuthStore();
  const clientsStore = useClientsStore();
  const networkStore = useNetworkStore();

  const connectedClinic = computed(() => authStore.connectedClinic);
  const currentClient = computed(() => authStore.currentClient);
  const userHasAccessToAllClients = computed(() => authStore.isManagerOrAdministrator);

  // Change page to Home if the client has been unselected but the current page requires a client
  watch(() => currentClient.value.id, async (value, prevValue) => {
    if (value || !prevValue || value === prevValue) {
      return;
    }

    if (router.currentRoute.value.meta?.requiresClient && !isEmpty(authStore.userDetails)) {
      router.push({ name: 'Home' });
    }
  });

  // Initialize deactivated client allowed to view
  const savedDeactivatedClientAllowedToView = JSON.parse(
    sessionStorage.getItem('deactivatedClientAllowedToView') || 'null',
  );
  if (savedDeactivatedClientAllowedToView) {
    authStore.changeDeactivatedClientAllowedToView(savedDeactivatedClientAllowedToView);
  }

  const deactivatedClientAllowedToView = computed(() => {
    return authStore.deactivatedClientAllowedToView;
  });
  // Reset current client if it was disabled
  watch(() => currentClient.value.status, async (value, prevValue) => {
    if (
      !value || value === prevValue || value !== 'inactive'
      // Don't reset staff client. A staff client should always be selected on app load
      || currentClient.value.type === 'staff'
      // Don't reset if the client is disabled, but it's allowed to be set as current client
      || (deactivatedClientAllowedToView.value) === currentClient.value.id
    ) {
      return;
    }

    await authStore.changeCurrentClient({});
    notifyWarning(i18n.t('clients.notifyUnselectDisabledClient'));

    if (router?.currentRoute?.value?.name === 'login' && !isEmpty(authStore.userDetails)) {
      try {
        await router.push({ name: 'Home' });
        await authStore.changeLoaderState(false);
      } catch (err) {
        logEvent(err);
        console.error(err);
      }
    }
  });

  /* Handle adding/removing clients for <= supervisor roles */

  /**
   * All the roles <= supervisor rely on the `usersClients` docs binding (storing it in the `associatedClients` vuex
   * field) which contains access to the specific client.
   */
  const associatedClients = computed(() => authStore.associatedClients);
  const associatedClientsTrigger = computed(() => {
    return associatedClients.value.map((userClient) => userClient.client).sort().toString();
  });
  const loadedClients = computed(() => clientsStore.loadedClients);

  // Associated clients were added or removed
  watch(associatedClientsTrigger, async (value, prevValue) => {
    if (
      !value || value === prevValue
      || userHasAccessToAllClients.value
    ) {
      return;
    }

    await clientsStore.syncClients();
    if (
      currentClient.value.id
      && !loadedClients.value.find(({ id }) => id === currentClient.value.id)
    ) {
      // The client was deleted or user has no access to it.
      await authStore.changeCurrentClient({});
      notifyWarning(i18n.t('clients.notifyUnselectDeletedOrNoAccessClient'));
    }
  });

  const managedClients = computed(() => clientsStore.managedClients);

  /**
   * `managedClients` were re-fetched by binding.
   * We don't need to use deep watch here as it completely overrides the array on each re-fetch
   */
  watch(managedClients, async () => {
    if (!userHasAccessToAllClients.value || !currentClient.value.id) {
      return;
    }

    const updatedCurrentClient = managedClients.value.find(({ id }) => id === currentClient.value.id);
    if (!updatedCurrentClient) {
      // Current client has been deleted.
      await authStore.changeCurrentClient({});
      notifyWarning(i18n.t('clients.notifyUnselectDeletedClient'));
    }

    // Current client could be changed and should be updated.
    authStore.updateCurrentClient(updatedCurrentClient);
  });

  /* Handle clients update notifications */

  /**
   * The query is initialized with `null` to prevent binding on the first render. Later the actual query
   * is set causing rebinding of already existing listener.
   *
   * todo: Implement GenericDB method to handle Composition API bindings.
   */
  const clientsNotificationsQuery = shallowRef(null);
  const clientsNotifications = useCollection(clientsNotificationsQuery);

  function bindClientsNotifications() {
    const realTimeNotificationsDb = new RealTimeNotificationsDB();
    clientsNotificationsQuery.value = realTimeNotificationsDb.getQuery([
      ['clinic', '==', connectedClinic.value.id],
      ['context', '==', RealTimeNotificationsDB.context.CLIENTS],
      ['type', '==', CLIENTS_NOTIFICATIONS_TYPES.CLIENT_UPDATED],
      ['createTimestamp', '>=', new Date()],
    ], 'createTimestamp', 'asc');
  } // bindClientsNotifications()

  function unbindClientsNotifications() {
    try {
      clientsNotificationsQuery.value = null;
    } catch (err) {
      console.error('Error unbinding notifications', err.message);
    }
  } // unbindClientsNotifications()

  const isOfflineMode = computed(() => networkStore.offlineMode);
  watch(() => connectedClinic.value.id, (value, prevValue) => {
    if (
      value === prevValue
      || userHasAccessToAllClients.value
      // There is no reason to bind such notifications in offline mode
      || isOfflineMode.value
    ) {
      return;
    }

    if (value) {
      bindClientsNotifications();
    } else {
      unbindClientsNotifications();
    }
  });

  const notificationsToIgnore = computed(() => (
    clientsStore.clientsTriggerNotificationsToIgnore
  ));
  const newClientNotifications = computed(() => {
    return clientsNotifications.value.filter((notification) => (
      // `notificationsToIgnore` includes notifications created by the current user from the current tab
      !notificationsToIgnore.value.includes(notification.id)
      // We don't need to reload non-associated clients
      && associatedClientsTrigger.value.includes(notification.client)
    ));
  });
  const newClientsNotificationsTrigger = computed(() => {
    return newClientNotifications.value
      .map((notification) => notification.id)
      .sort()
      .toString();
  });

  /**
   * @param {Record<string, number> | Object} viewedNotificationsMap
   * @param {Object[]} newNotifications
   */
  function addNotificationsToViewedMap(viewedNotificationsMap, newNotifications) {
    const nowDate = Date.now();
    const viewedClientsNotificationsMap = newNotifications.reduce((acc, notification) => {
      acc[notification.id] = nowDate;
      return acc;
    }, {});

    return Object.assign(viewedNotificationsMap, viewedClientsNotificationsMap);
  } // addNotificationsToViewed()

  async function processClientsNotifications() {
    let newClientsNotifications = newClientNotifications.value;

    // Check local storage for viewed notifications in order to filter out the ones that are already viewed
    const initialViewedNotificationsMap = JSON.parse(localStorage.getItem('viewedTriggerNotifications') ?? '{}');
    let viewedNotificationsMap = cloneDeep(initialViewedNotificationsMap);
    const viewedNotificationsIds = Object.keys(viewedNotificationsMap);

    if (viewedNotificationsIds.length > 0) {
      newClientsNotifications = newClientsNotifications.filter((notification) => {
        return !(viewedNotificationsMap[notification.id]);
      });

      // Clear old (> 4 hours) viewed notification records from local storage to save LS memory
      const oneDayInMs = 14_400_000;
      const nowDate = Date.now();
      viewedNotificationsIds.forEach((notificationId) => {
        const viewedDate = viewedNotificationsMap[notificationId];

        if ((nowDate - oneDayInMs) > viewedDate) {
          delete viewedNotificationsMap[notificationId];
        }
      });
    }

    if (newClientsNotifications.length > 0) {
      viewedNotificationsMap = addNotificationsToViewedMap(viewedNotificationsMap, newClientsNotifications);

      const newClientNotificationsActions = chain(newClientsNotifications)
        .uniqBy('client')
        .map((notification) => (
          clientsStore.syncClient(notification.client)
        ))
        .value();
      await AppUtils.allSettled(newClientNotificationsActions);
    }

    if (!isEqual(viewedNotificationsMap, initialViewedNotificationsMap)) {
      localStorage.setItem('viewedTriggerNotifications', JSON.stringify(viewedNotificationsMap));
    }
  } // processClientsNotifications()

  watch(newClientsNotificationsTrigger, (value, prevValue) => {
    if (value.length === 0 || value === prevValue) {
      return;
    }

    processClientsNotifications();
  });

  return {};
}
